import React, { useState, useEffect } from 'react';
import TimeTracker from './TimeTracker';
import ReviewTasksPanel from './ReviewTasksPanel';
import { API_URL } from '../../constants/appConstants';
import { WeeklyTimeSummary } from '../../types';
import './TimeTrackerPage.css';

type TabType = 'tracker' | 'review';

const TimeTrackerPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('tracker');
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [reviewCount, setReviewCount] = useState<number>(0);
  const [timeSummary, setTimeSummary] = useState<WeeklyTimeSummary | null>(null);
  const [timeSummaryLoading, setTimeSummaryLoading] = useState(true);

  // Fetch the logged-in user's employee ID
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${API_URL}/intranet/me`, { credentials: 'include' });
        if (response.ok) {
          const user = await response.json();
          setEmployeeId(user.id || null);
          
          // Fetch review count for badge
          if (user.id) {
            const reviewResponse = await fetch(
              `${API_URL}/intranet/tasks/${user.id}/review`,
              { credentials: 'include' }
            );
            if (reviewResponse.ok) {
              const reviewData = await reviewResponse.json();
              setReviewCount(reviewData.totalCount || 0);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching user info:', err);
      }
    };
    
    fetchUser();
  }, []);

  // Fetch weekly time summary when we have an employee ID
  useEffect(() => {
    if (!employeeId) return;

    const fetchTimeSummary = async () => {
      setTimeSummaryLoading(true);
      try {
        const response = await fetch(
          `${API_URL}/intranet/time-summary/${employeeId}`,
          { credentials: 'include' }
        );
        if (response.ok) {
          const data = await response.json();
          setTimeSummary(data);
        }
      } catch (err) {
        console.error('Error fetching time summary:', err);
      } finally {
        setTimeSummaryLoading(false);
      }
    };

    fetchTimeSummary();
    // Refresh every 5 minutes
    const interval = setInterval(fetchTimeSummary, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [employeeId]);

  return (
    <div className="time-tracker-page">
      <div className="page-header">
        <h1>Time Tracker</h1>
        <p className="page-description">Track your work hours and manage time entries</p>
      </div>

      {/* Tab Navigation */}
      <div className="tracker-tabs">
        <button
          className={`tracker-tab ${activeTab === 'tracker' ? 'active' : ''}`}
          onClick={() => setActiveTab('tracker')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
          Daily Manager
        </button>
        <button
          className={`tracker-tab ${activeTab === 'review' ? 'active' : ''}`}
          onClick={() => setActiveTab('review')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 11l3 3L22 4"></path>
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
          </svg>
          Review Tasks
          {reviewCount > 0 && (
            <span className="tab-badge">{reviewCount}</span>
          )}
        </button>
      </div>

      <div className="tracker-layout">
        <div className="tracker-main">
          {activeTab === 'tracker' ? (
          <TimeTracker />
          ) : (
            <ReviewTasksPanel 
              employeeId={employeeId} 
              onCountChange={setReviewCount}
            />
          )}
        </div>

        <div className="tracker-sidebar">
          <div className="sidebar-card">
            <h3>Weekly Summary</h3>
            {timeSummaryLoading ? (
              <div className="weekly-loading">Loading...</div>
            ) : timeSummary ? (
            <div className="weekly-stats">
              <div className="weekly-stat">
                <span className="weekly-label">This Week</span>
                  <span className="weekly-value">
                    {Math.floor(timeSummary.taskHoursTotal)}h {Math.round((timeSummary.taskHoursTotal % 1) * 60)}m
                  </span>
              </div>
              <div className="weekly-stat">
                <span className="weekly-label">Target</span>
                <span className="weekly-value">40h 00m</span>
              </div>
                <div className="weekly-stat">
                  <span className="weekly-label">Daily Avg</span>
                  <span className={`weekly-value ${timeSummary.dailyAverage >= 7 ? 'good' : timeSummary.dailyAverage >= 5 ? 'warning' : 'low'}`}>
                    {timeSummary.dailyAverage.toFixed(1)}h
                  </span>
                </div>
              <div className="weekly-progress">
                <div className="progress-bar">
                    <div 
                      className={`progress-fill ${timeSummary.utilizationPercent >= 90 ? 'good' : timeSummary.utilizationPercent >= 70 ? 'warning' : 'low'}`}
                      style={{ width: `${Math.min(timeSummary.utilizationPercent, 100)}%` }}
                    ></div>
                  </div>
                  <span className="progress-label">{timeSummary.utilizationPercent}% of expected ({timeSummary.workdaysPassed} day{timeSummary.workdaysPassed !== 1 ? 's' : ''})</span>
                </div>
                
                {/* Daily breakdown bars */}
                <div className="weekly-daily-breakdown">
                  {(() => {
                    const weekStart = new Date(timeSummary.weekStart);
                    const today = new Date();
                    today.setHours(12, 0, 0, 0);
                    
                    const formatTime = (seconds: number) => {
                      const h = Math.floor(seconds / 3600);
                      const m = Math.floor((seconds % 3600) / 60);
                      return h > 0 ? `${h}h ${m}m` : `${m}m`;
                    };
                    
                    const days = [];
                    for (let i = 0; i < 7; i++) {
                      const day = new Date(weekStart);
                      day.setDate(weekStart.getDate() + i);
                      day.setHours(12, 0, 0, 0);
                      const dateStr = `${(day.getMonth() + 1).toString().padStart(2, '0')}/${day.getDate().toString().padStart(2, '0')}/${day.getFullYear()}`;
                      const dayName = day.toLocaleDateString('en-US', { weekday: 'short' });
                      const dayLetter = dayName.charAt(0);
                      const isToday = day.toDateString() === today.toDateString();
                      const isFuture = day > today;
                      const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                      const data = timeSummary.tasksByDay[dateStr];
                      const hours = data ? data.timeSeconds / 3600 : 0;
                      
                      days.push({ dateStr, dayName, dayLetter, isToday, isFuture, isWeekend, hours, data });
                    }
                    
                    return days.map(({ dateStr, dayName, dayLetter, isToday, isFuture, isWeekend, hours, data }) => (
                      <div 
                        key={dateStr}
                        className={`daily-mini-bar ${isToday ? 'today' : ''} ${isFuture ? 'future' : ''} ${isWeekend ? 'weekend' : ''}`}
                      >
                        <div 
                          className={`daily-mini-fill ${hours >= 7 ? 'good' : hours >= 4 ? 'warning' : hours > 0 ? 'low' : ''}`}
                          style={{ height: `${Math.min((hours / 8) * 100, 100)}%` }}
                        />
                        <span className="daily-mini-label">{dayLetter}</span>
                        
                        {/* Hover tooltip with task details */}
                        <div className="daily-mini-tooltip">
                          <div className="tooltip-header">
                            <strong>{dayName}</strong>
                            <span className="tooltip-hours">{hours.toFixed(2)}h</span>
                          </div>
                          {data && data.taskList && data.taskList.length > 0 ? (
                            <div className="tooltip-tasks">
                              {data.taskList.slice(0, 8).map((task: any, idx: number) => (
                                <div key={idx} className={`tooltip-task ${task.isRunning ? 'running' : ''}`}>
                                  <span className="tooltip-task-time">
                                    {task.timeHours || formatTime(task.timeSeconds)}
                                  </span>
                                  <span className={`tooltip-task-type type-${task.type?.toLowerCase()}`}>
                                    {task.type}
                                  </span>
                                  <span className="tooltip-task-desc" title={task.description}>
                                    {task.description.length > 30 
                                      ? task.description.substring(0, 30) + '...' 
                                      : task.description}
                                  </span>
                                </div>
                              ))}
                              {data.taskList.length > 8 && (
                                <div className="tooltip-more">+{data.taskList.length - 8} more</div>
                              )}
                            </div>
                          ) : (
                            <div className="tooltip-empty">
                              {isFuture ? 'Future' : isWeekend ? 'Weekend' : 'No tasks'}
                            </div>
                          )}
              </div>
            </div>
                    ));
                  })()}
          </div>
              </div>
            ) : (
              <div className="weekly-error">Could not load time data</div>
            )}
          </div>

          <div className="sidebar-card tips-card">
            <h3>Tips</h3>
            <ul className="tips-list">
              <li>Select a project before starting the timer</li>
              <li>Add descriptions to track what you worked on</li>
              <li>Review your daily log before signing off</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeTrackerPage;

